import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { ErrorMessage } from "formik";
import { Grid, RadioGroup, Radio, Box } from "@mui/material";
import { FormControl, FormControlLabel } from "@mui/material";

import { AddCircle as AddCircleIcon } from "@mui/icons-material";

import { CommonService } from "../../store/common/CommonSlice";
import { CommonUtil, LoginUserDetails } from "../../util/Utils";

import Field from "../../component/Field";
import Label from "../../component/Label";
import SelectField from "../../component/SelectField";
import Toaster from "../../component/Toaster";
import SimpleDate from "../../component/simpleDate";
import ModalDialog from "../../component/Modal";
import Addschool from "../../component/AddSchool";
import GlobalLoader from "../../component/GlobalLoader";
import { GENDER } from "../../util/LookupConst";
import EditableSelectField from "../../component/editableSelectField";
import { calculateAge, countryWiseDateFormate } from "../../util/DateTimeUtils";

type SetFieldValue = (name: string, value: string | number | boolean) => number;

interface IStudentInfoProps {
  handleDrawerClose?: any;
  handleDrawerOpen?: any;
  setModalOpenSchool?: any;
  values: any;
  handleBlur: any;
  errors: any;
  touched: any;
  setFieldValue: any;
  handleChange: any;
  refferedByOptions: any;
  sourceOfEnquiryOptions: any;
  registrationTypeOptions: any;
  handleChangeInitialValue: any;
  courseNameOptions: any;
}

const StudentInfo = (props: IStudentInfoProps) => {
  const dateFormat = process.env.REACT_APP_DEFAULT_DATE_FORMAT;
  const dispatch = useDispatch<any>();
  const ref = useRef(true);
  const { t } = useTranslation();

  const { values, errors, touched, refferedByOptions } = props;
  const { handleChange, handleBlur, setFieldValue, courseNameOptions } = props;
  const { sourceOfEnquiryOptions, registrationTypeOptions } = props;
  const { handleChangeInitialValue } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [age, setAge] = useState<any>("");
  const [school, setSchool] = useState<any>([]);
  const [sourceOfEnquiry, setSourceOfEnquiry] = useState("");
  const [referredBy, setReferredBy] = useState("");
  const [spinner, setSpinner] = useState(false);

  useEffect(() => {
    if (values.sourceOfEnquiryId) {
      setSourceOfEnquiry(getSourceOfEnquiryById(values.sourceOfEnquiryId));
      setReferredBy(getSourceOfEnquiryById(values.sourceOfEnquiryId));
    }
  }, [values.sourceOfEnquiryId]);

  useEffect(() => {
    if (values.referredById) {
      setReferredBy(getRefferedByWithId(values.referredById));
    }
  }, [values.referredById]);

  useEffect(() => {
    if (ref.current) {
      ref.current = false;
      getSchoolList();
    }
  });

  const handleModalOpen = () => {
    setModalOpen(!modalOpen);
    //props.handleDrawerClose();
    if (props?.setModalOpenSchool) {
      props?.setModalOpenSchool();
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleDateChange = (date: any) => {
    if (date) {
      const calculatedAge = calculateAge(date)
      setAge(calculatedAge);
    } else {
      setAge(0);
    }
  };

  const schoolNameAdded = (data: any, schoolName: string) => {
    if (data.status === 200) {
      getSchoolList().then((schoolData: any) => {
        handleSchoolName(schoolData.find((s: any) => s.label === schoolName));
      });
      setModalOpen(false);
    }
  };

  const getSchoolList = async () => {
    setSpinner(true);
    let schoolData: any;
    await dispatch(CommonService({ apiAction: "School" }))
      .then((result: any) => {
        if (result?.payload?.status === 200) {
          schoolData = CommonUtil.formateDropdownList(
            result?.payload?.data.data,
            "schoolName"
          );
          setFieldValue(
            "schoolName",
            schoolData?.find((item: any) => item?.value === values?.schoolId)
          );
          setSchool(schoolData);
        } else {
          Toaster.error(undefined, result.payload);
        }
      })
      .catch((err: any) => {
        console.log(err); //This console for debug the app if any error occurred in DEV
      })
      .finally(() => {
        setSpinner(false);
      });
    return schoolData;
  };

  const url = window.location.href;
  const urlArr = url.split("/");
  const findUrl = urlArr[urlArr.length - 1];

  const getRegistrationTypeById = (registrationTypeId: any) => {
    const filterredRegistrationType = registrationTypeOptions.filter(
      (item: any) => {
        return item.id === Number(registrationTypeId);
      }
    );
    return (
      filterredRegistrationType &&
      filterredRegistrationType.length > 0 &&
      filterredRegistrationType[0].name
    );
  };

  const handleRadioChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldValue: SetFieldValue
  ) => {
    const registrationType = getRegistrationTypeById(event.target.value);

    fieldValue("registrationType", registrationType);
    fieldValue("registrationTypeId", (event.target as HTMLInputElement).value);

    handleChangeInitialValue({
      ...values,
      registrationTypeId: event.target.value,
      registrationType: registrationType,
      levelOfAdmissionId: undefined,
    });
  };

  const getSourceOfEnquiryById = (sourceOfEnquiryId: any) => {
    const filterredSourceOfEnquiries = sourceOfEnquiryOptions.filter(
      (item: any) => {
        return item.value === sourceOfEnquiryId;
      }
    );
    return (
      filterredSourceOfEnquiries &&
      filterredSourceOfEnquiries.length > 0 &&
      filterredSourceOfEnquiries[0].label
    );
  };

  const getRefferedByWithId = (referredById: any) => {
    const filterredRefferedBy = refferedByOptions.filter((item: any) => {
      return item.value === referredById;
    });
    return (
      filterredRefferedBy &&
      filterredRefferedBy.length > 0 &&
      filterredRefferedBy[0].label
    );
  };

  const handleSourceOfEnquiry = (event: any, fieldValue: SetFieldValue) => {
    fieldValue("sourceOfEnquiryId", Number(event.target.value));
    const selectedSourceOfEnquiry = getSourceOfEnquiryById(event.target.value);
    if (selectedSourceOfEnquiry !== "Reference") {
      fieldValue("referredBy", "");
      fieldValue("referredById", "");
    }
    const selectedRefferedBy = getRefferedByWithId(event.target.value);
    if (selectedRefferedBy !== "Others") {
      fieldValue("otherInfo", "");
    }
    setReferredBy(selectedRefferedBy);
    setSourceOfEnquiry(selectedSourceOfEnquiry);
  };

  const handleRefferedByChange = (event: any, fieldValue: SetFieldValue) => {
    fieldValue("referredById", Number(event.target.value));
    const selectedRefferedBy = getRefferedByWithId(event.target.value);
    if (selectedRefferedBy !== "Others") {
      fieldValue("otherInfo", "");
    }
    setReferredBy(selectedRefferedBy);
  };

  const handleSchoolName = (schoolName: any) => {
    setFieldValue("schoolName", schoolName);
    setFieldValue("schoolId", schoolName.value);
  };

  const handleCourseChange = (value: number, setFieldValue: any) => {
    setFieldValue("courseId", value);
    setFieldValue("levelOfAdmissionId", undefined);
    setFieldValue("courseInstructor", undefined);
    setFieldValue("courseInstructorId", undefined);
    setFieldValue("batchId", undefined);
    setFieldValue("countryId", "");
    values.countryId = "";
    // getLevelOfAdmissionListByCourseId(value);
  };

  return (
    <div>
      <Grid className="sidenav-container">
        <Box className="full">
          <Grid
            container
            rowSpacing={1}
            columnSpacing={{ xs: 1, sm: 1, md: 3 }}
          >
            {findUrl === "student-master" && (
              <Grid item xs={12}>
                <div className="margin-down-10">
                  <div>
                    <Label title={t("registrationType")} />
                  </div>

                  <FormControl>
                    <RadioGroup
                      row
                      aria-labelledby="demo-row-radio-buttons-group-label"
                      name="registrationTypeId"
                      value={values.registrationTypeId}
                      onChange={(e: any) => {
                        handleRadioChange(e, setFieldValue);
                      }}
                    >
                      {registrationTypeOptions &&
                        registrationTypeOptions.length > 0 &&
                        registrationTypeOptions.map((item: any, i: number) => (
                          <FormControlLabel
                            value={item.id}
                            control={<Radio />}
                            label={item.name}
                          />
                        ))}
                    </RadioGroup>
                  </FormControl>
                </div>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <div style={{ paddingRight: "20px" }}>
                <div className="autocomplete-form">
                  <Label required title={t("selectCourseName")} />
                  <SelectField
                    name="courseId"
                    value={values.courseId}
                    onChange={(e: any) => {
                      handleCourseChange(e.target.value, setFieldValue);
                      // setBatchOptions([]);
                    }}
                    options={courseNameOptions}
                  />
                </div>
                <div>
                  <Label required title={t("studentName")} />
                  <Field
                    className="textFiled"
                    name="studentName"
                    placeholder={t("enterHere")}
                    value={values.studentName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    max={255}
                    helperText={<ErrorMessage name="studentName" />}
                    error={errors.studentName && touched.studentName}
                  />
                </div>
                <div className="flex form-ele-gap">
                  <div>
                    {LoginUserDetails.getCountry() === "India" && (
                      <Label required title={t("dob(DD/MM/YYYY)")} />
                      )}
                      {LoginUserDetails.getCountry() !== "India" && (
                      <Label required title={t("DOB")} />
                      )}
                    <SimpleDate
                      className="textFiled"
                      name="dateOfBirth"
                      initialValue={values.dateOfBirth}
                      value={values.dateOfBirth}
                      format={countryWiseDateFormate()}
                      onChange={(e: any) => {
                        setFieldValue("dateOfBirth", e);
                        handleDateChange(e);
                      }}
                      onBlur={handleBlur}
                      disableFuture
                      helperText={<ErrorMessage name="dateOfBirth" />}
                      error={errors.dateOfBirth && touched.dateOfBirth}
                    />
                  </div>
                  <div className="padding-left-10">
                    <Label title={t("age")} />
                    <Field
                      className="textFiled"
                      disabled
                      name="age"
                      value={age}
                    />
                  </div>
                </div>
                <div>
                  <div className="form-ele-gap autocomplete-form">
                    <Label required title={t("gender")} />
                    <SelectField
                      style={{ marginTop: "8px" }}
                      name="gender"
                      value={values.gender || t("select")}
                      onChange={handleChange}
                      options={GENDER}
                      helperText={<ErrorMessage name="gender " />}
                      error={errors.gender && touched.gender}
                    />
                  </div>
                </div>
                <div className="form-ele-gap">
                  <Label required title={t("schoolName")} />
                  <EditableSelectField
                    style={{ marginTop: "8px" }}
                    name="schoolName"
                    value={values.schoolName || t("select")}
                    onChange={(value: any) => {
                      handleSchoolName(value);
                    }}
                    onBlur={handleBlur}
                    options={school}
                    helperText={<ErrorMessage name="courseInstructorId" />}
                    error={errors.schoolName && touched.schoolName}
                  />
                </div>
                <div
                  className="center-end link-black"
                  style={{ paddingTop: "10px" }}
                  onClick={handleModalOpen}
                >
                  <AddCircleIcon
                    className="color-sec"
                    style={{ fontSize: "18px", margin: "0px" }}
                  />
                  <span className="font-14">{t("addSchool")}</span>
                </div>
              </div>
            </Grid>
            <Grid xs={12} sm={6} md={6} lg={6}>
              <div style={{ paddingTop: "7px" }}>
                {/* {Number(LoginUserDetails.getUserDetails().productId === 1) ? (
                  <div className="inherit-number-field">
                    <Label
                      title={t("BrandPromiseScore")}
                    />
                    <Field
                      className="textFiled"
                      name="brandPromiseScore"
                      placeholder={t("enterHere")}
                      value={values?.brandPromiseScore}
                      type="number"
                      onInput={(e: any) => CommonUtil.checkLengthHandler(e, 2)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      onKeyUp={(e: any) => {
                        if (Number(e.target.value) > 50)
                          setFieldValue("brandPromiseScore", 50);
                      }}
                      helperText={<ErrorMessage name="brandPromiseScore" />}
                      error={
                        errors.brandPromiseScore && touched.brandPromiseScore
                      }
                    />
                  </div>
                ) : (
                  <></>
                )} */}
                <div className="inherit-number-field">
                  <Label title={t("class")} />
                  <Field
                    className="textFiled"
                    name="className"
                    placeholder={t("enterHere")}
                    value={values?.className}
                    type="number"
                    onInput={(e: any) => CommonUtil.checkLengthHandler(e, 2)}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
                <div
                  className={"autocomplete-form  form-ele-gap"}
                  style={{ paddingBottom: "2px" }}
                >
                  <Label required title={t("sourceOfEnquiry")} />
                  <SelectField
                    name="sourceOfEnquiryId"
                    value={values?.sourceOfEnquiryId || t("select")}
                    onChange={(e: any) => {
                      handleSourceOfEnquiry(e, setFieldValue);
                      handleChange(e);
                    }}
                    options={sourceOfEnquiryOptions}
                  />
                </div>

                <div className="form-ele-gap autocomplete-form">
                  <Label
                    title={t("referredBy")}
                    required={
                      sourceOfEnquiryOptions.find(
                        (soe: any) => soe.label === "Reference"
                      )?.value === Number(values?.sourceOfEnquiryId)
                    }
                  />
                  <SelectField
                    disabled={sourceOfEnquiry !== "Reference"}
                    name="referredById"
                    value={values?.referredById || t("select")}
                    onBlur={handleBlur}
                    onChange={(e: any) => {
                      handleRefferedByChange(e, setFieldValue);
                      handleChange(e);
                    }}
                    options={refferedByOptions}
                    helperText={<ErrorMessage name="referredById" />}
                    error={errors.referredById && touched.referredById}
                  />
                </div>
                <div className="form-ele-gap">
                  <Label
                    title={t("otherInfo")}
                    required={
                      sourceOfEnquiryOptions.find(
                        (soe: any) => soe.label === "Others"
                      )?.value === Number(values?.sourceOfEnquiryId) ||
                      refferedByOptions.find(
                        (soe: any) => soe.label === "Others"
                      )?.value === Number(values?.referredById)
                    }
                  />
                  <Field
                    name="otherInfo"
                    className="textFiled"
                    disabled={referredBy !== "Others"}
                    placeholder={t("enterHere")}
                    value={values?.otherInfo}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={<ErrorMessage name="otherInfo" />}
                    error={errors.otherInfo && touched.otherInfo}
                  />
                </div>
              </div>
            </Grid>
          </Grid>

          {/* modal */}
          <div>
            {findUrl === "student-master" || "student-lead-management" ? (
              <>
                <ModalDialog
                  state={modalOpen}
                  title={t("addSchool")}
                  onClose={handleModalClose}
                  size="s"
                  content={
                    <Addschool
                      schoolNameAdded={schoolNameAdded}
                      handleModalClose={handleModalClose}
                    />
                  }
                ></ModalDialog>
              </>
            ) : (
              <ModalDialog
                state={modalOpen}
                title={t("addSchool")}
                onClose={handleModalClose}
                size="s"
                content={
                  <Addschool
                    schoolNameAdded={schoolNameAdded}
                    handleModalClose={handleModalClose}
                  />
                }
              ></ModalDialog>
            )}
          </div>
        </Box>
        <GlobalLoader loading={spinner} />
      </Grid>
    </div>
  );
};

export default StudentInfo;
